import React from 'react';
import Header from '../../components/ui/Header';
import Footer from '../../components/ui/Footer';
import Breadcrumbs from '../../components/ui/Breadcrumbs';

const Careers = () => {
  const jobOpenings = [
    {
      title: 'Senior Real Estate Agent',
      location: 'Kampala, Uganda',
      type: 'Full-time',
      salary: 'UGX 2M - 5M',
      description: 'Join our team of expert real estate professionals helping clients find their dream properties.',
      requirements: ['5+ years experience', 'Real estate license', 'Strong negotiation skills']
    },
    {
      title: 'Frontend Developer',
      location: 'Remote',
      type: 'Full-time',
      salary: 'UGX 3M - 7M',
      description: 'Build amazing user experiences for our property search platform using modern web technologies.',
      requirements: ['React expertise', '3+ years experience', 'TypeScript knowledge']
    },
    {
      title: 'Marketing Specialist',
      location: 'Kampala, Uganda',
      type: 'Full-time',
      salary: 'UGX 2.5M - 4M',
      description: 'Drive growth and engagement through innovative marketing campaigns and digital strategies.',
      requirements: ['Digital marketing experience', 'Social media expertise', 'Analytics skills']
    },
    {
      title: 'Customer Success Manager',
      location: 'Kampala, Uganda',
      type: 'Full-time',
      salary: 'UGX 2M - 4M',
      description: 'Ensure our clients have exceptional experiences throughout their property journey.',
      requirements: ['Customer service experience', 'Communication skills', 'Problem-solving ability']
    }
  ];

  const benefits = [
    {
      icon: 'Heart',
      title: 'Health & Wellness',
      description: 'Comprehensive health insurance and wellness programs'
    },
    {
      icon: 'GraduationCap',
      title: 'Learning & Development',
      description: 'Continuous learning opportunities and professional development'
    },
    {
      icon: 'Home',
      title: 'Work-Life Balance',
      description: 'Flexible working hours and remote work options'
    },
    {
      icon: 'TrendingUp',
      title: 'Career Growth',
      description: 'Clear career progression paths and mentorship programs'
    },
    {
      icon: 'Coffee',
      title: 'Great Perks',
      description: 'Free lunch, team outings, and modern office facilities'
    },
    {
      icon: 'DollarSign',
      title: 'Competitive Salary',
      description: 'Market-leading compensation and performance bonuses'
    }
  ];

  const stats = [
    { number: '95%', label: 'Employee Satisfaction' },
    { number: '50+', label: 'Team Members' },
    { number: '12+', label: 'Countries Represented' },
    { number: '25+', label: 'Avg. Years Experience' }
  ];

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <main className="pt-16">
        {/* Hero Section */}
        <section className="relative overflow-hidden bg-gradient-to-br from-primary/5 via-primary/10 to-secondary/5">
          <div className="absolute inset-0 opacity-40" style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%239C92AC' fill-opacity='0.05'%3E%3Ccircle cx='30' cy='30' r='4'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E"), radial-gradient(circle at 12% 18%, rgba(59,130,246,0.12), transparent 24%), radial-gradient(circle at 78% 20%, rgba(236,72,153,0.14), transparent 24%)`
          }}></div>
          <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24">
            <div className="absolute -left-16 top-20 w-64 h-64 rounded-full bg-primary/20 blur-3xl"></div>
            <div className="absolute -right-16 bottom-16 w-72 h-72 rounded-full bg-secondary/20 blur-3xl"></div>
            <Breadcrumbs />
            <div className="text-center">
              <div className="inline-flex items-center px-4 py-2 rounded-full bg-primary/10 text-primary text-sm font-medium mb-6">
                <span className="w-2 h-2 bg-primary rounded-full mr-2 animate-pulse"></span>
                Join Our Team
              </div>
              <h1 className="text-5xl md:text-6xl font-bold text-foreground mb-6 leading-tight">
                Shape the Future of
                <span className="block text-primary">Real Estate</span>
              </h1>
              <p className="text-xl text-muted-foreground max-w-3xl mx-auto leading-relaxed">
                Join a passionate team that's revolutionizing property transactions in East Africa.
                We're looking for innovative thinkers who want to make a real impact.
              </p>
            </div>
          </div>
        </section>

        {/* Stats Section */}
        <section className="py-16 bg-card/50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
              {stats.map((stat, index) => (
                <div key={index} className="text-center group">
                  <div className="text-3xl md:text-4xl font-bold text-primary mb-2 group-hover:scale-110 transition-transform duration-300">
                    {stat.number}
                  </div>
                  <div className="text-muted-foreground font-medium">{stat.label}</div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Why Join Us */}
        <section className="py-20">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <h2 className="text-3xl font-bold text-foreground mb-4">Why Join HireBroker?</h2>
              <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
                We're more than just a workplace - we're a community of innovators, problem-solvers, and real estate enthusiasts
              </p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {benefits.map((benefit, index) => (
                <div key={index} className="bg-card rounded-2xl p-8 shadow-sm hover:shadow-xl transition-all duration-300 hover:-translate-y-2 group">
                  <div className="w-16 h-16 bg-gradient-to-br from-primary/20 to-primary/10 rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300">
                    <svg className="w-8 h-8 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      {benefit.icon === 'Heart' && <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />}
                      {benefit.icon === 'GraduationCap' && <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 14l9-5-9-5-9 5 9 5z M12 14l6.16-3.422a12.083 12.083 0 01.665 6.479A11.952 11.952 0 0012 20.055a11.952 11.952 0 00-6.824-2.998 12.078 12.078 0 01.665-6.479L12 14z M12 14l-9-5 9-5 9 5-9 5zm0 0l6.16-3.422a12.083 12.083 0 01.665 6.479A11.952 11.952 0 0012 20.055a11.952 11.952 0 00-6.824-2.998 12.078 12.078 0 01.665-6.479L12 14zm-4 6v-7.5l4-2.222" />}
                      {benefit.icon === 'Home' && <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />}
                      {benefit.icon === 'TrendingUp' && <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />}
                      {benefit.icon === 'Coffee' && <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8h1a4 4 0 110 8H7a4 4 0 110-8h1M17 12v4a2 2 0 01-2 2H9a2 2 0 01-2-2v-4m8-8V6a2 2 0 00-2-2H9a2 2 0 00-2 2v4.01" />}
                      {benefit.icon === 'DollarSign' && <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 1v22m11-11H1m11 0a4.5 4.5 0 100-9 4.5 4.5 0 000 9z" />}
                    </svg>
                  </div>
                  <h3 className="text-xl font-semibold text-foreground mb-3">{benefit.title}</h3>
                  <p className="text-muted-foreground">{benefit.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Open Positions */}
        <section className="py-20 bg-muted/30">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <h2 className="text-3xl font-bold text-foreground mb-4">Open Positions</h2>
              <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
                Find your next career opportunity and join our mission to transform real estate in East Africa
              </p>
            </div>

            <div className="grid lg:grid-cols-2 gap-8">
              {jobOpenings.map((job, index) => (
                <div key={index} className="bg-card rounded-2xl p-8 shadow-sm hover:shadow-xl transition-all duration-300 hover:-translate-y-1">
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex-1">
                      <h3 className="text-xl font-semibold text-foreground mb-2">{job.title}</h3>
                      <div className="flex flex-wrap items-center gap-3 mb-3">
                        <span className="inline-flex items-center px-3 py-1 rounded-full bg-primary/10 text-primary text-sm font-medium">
                          {job.type}
                        </span>
                        <span className="text-muted-foreground text-sm">{job.location}</span>
                        <span className="text-primary font-medium text-sm">{job.salary}</span>
                      </div>
                    </div>
                  </div>

                  <p className="text-muted-foreground mb-4">{job.description}</p>

                  <div className="mb-6">
                    <h4 className="font-semibold text-foreground mb-2">Requirements:</h4>
                    <ul className="text-sm text-muted-foreground space-y-1">
                      {job.requirements.map((req, reqIndex) => (
                        <li key={reqIndex} className="flex items-center">
                          <svg className="w-4 h-4 text-primary mr-2 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                          </svg>
                          {req}
                        </li>
                      ))}
                    </ul>
                  </div>

                  <button className="w-full bg-primary text-primary-foreground px-6 py-3 rounded-xl hover:bg-primary/90 transition-colors duration-200 font-semibold shadow-lg hover:shadow-xl">
                    Apply Now
                  </button>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Culture Section */}
        <section className="py-20">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid lg:grid-cols-2 gap-16 items-center">
              <div>
                <h2 className="text-3xl font-bold text-foreground mb-6">Our Culture</h2>
                <p className="text-lg text-muted-foreground mb-8 leading-relaxed">
                  At HireBroker, we believe in fostering an environment where creativity thrives,
                  collaboration is encouraged, and every team member feels valued and empowered
                  to make a difference.
                </p>

                <div className="space-y-4">
                  <div className="flex items-start gap-3">
                    <div className="w-8 h-8 bg-primary/10 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5">
                      <svg className="w-4 h-4 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                      </svg>
                    </div>
                    <div>
                      <h4 className="font-semibold text-foreground">Innovation First</h4>
                      <p className="text-muted-foreground text-sm">We embrace cutting-edge solutions and encourage creative problem-solving.</p>
                    </div>
                  </div>

                  <div className="flex items-start gap-3">
                    <div className="w-8 h-8 bg-primary/10 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5">
                      <svg className="w-4 h-4 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                      </svg>
                    </div>
                    <div>
                      <h4 className="font-semibold text-foreground">Inclusive Environment</h4>
                      <p className="text-muted-foreground text-sm">We celebrate diversity and create opportunities for everyone to succeed.</p>
                    </div>
                  </div>

                  <div className="flex items-start gap-3">
                    <div className="w-8 h-8 bg-primary/10 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5">
                      <svg className="w-4 h-4 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                    </div>
                    <div>
                      <h4 className="font-semibold text-foreground">Impact Driven</h4>
                      <p className="text-muted-foreground text-sm">We measure success by the positive change we create in our community.</p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="relative">
                <div className="aspect-square rounded-3xl bg-gradient-to-br from-primary/20 via-primary/10 to-secondary/20 flex items-center justify-center">
                  <div className="text-center">
                    <div className="w-20 h-20 bg-primary/20 rounded-full flex items-center justify-center mx-auto mb-6">
                      <svg className="w-10 h-10 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14.828 14.828a4 4 0 01-5.656 0M9 10h1.586a1 1 0 01.707.293l.707.707A1 1 0 0012.414 11H15m2 0h1.586a1 1 0 01.707.293l.707.707A1 1 0 0020.414 12H21M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                    </div>
                    <h3 className="text-2xl font-bold text-foreground mb-2">Work With Us</h3>
                    <p className="text-muted-foreground">Join a team that's passionate about transforming real estate</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-20 bg-gradient-to-r from-primary/5 to-secondary/5">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h2 className="text-3xl font-bold text-foreground mb-4">Ready to Join Our Team?</h2>
            <p className="text-lg text-muted-foreground mb-8">
              Don't see a position that matches your skills? We're always interested in meeting talented individuals.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button className="inline-flex items-center px-8 py-4 bg-primary text-primary-foreground rounded-xl hover:bg-primary/90 transition-colors duration-200 font-semibold shadow-lg hover:shadow-xl">
                Send Your Resume
              </button>
              <a
                href="/contact"
                className="inline-flex items-center px-8 py-4 border border-border rounded-xl hover:bg-card transition-colors duration-200 font-semibold"
              >
                Contact Us
              </a>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
};

export default Careers;